import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import {
    mdiChevronDown,
    mdiChevronRight,
    mdiFolderOutline,
    mdiFolderOpenOutline,
    mdiFolderRemoveOutline,
} from '@mdi/js'
import type { ImportFolderNode } from './importTypes'
import { EmptyState } from '../ui/empty-state'

interface ImportDepositTreeProps {
    org: string
    importUid: string
    tree: ImportFolderNode[]
    /**
     * Set when the root list itself overflowed the 200-folder cap. Those
     * folders are absent from `tree` altogether — no node carries their count —
     * so the view says how much it is not showing.
     */
    truncated?: boolean
    omittedFolders?: number
    omittedDocuments?: number
}

interface ImportDepositTreeNodeProps {
    org: string
    importUid: string
    node: ImportFolderNode
    depth: number
}

/** Top-level folders start open; anything deeper starts collapsed. */
const DEFAULT_OPEN_DEPTH = 1

/**
 * One folder row: the disclosure caret, the folder icon, the name, the
 * recursive document count, and — for a real folder — the link into the
 * documents list filtered on that path.
 *
 * The synthetic "unfiled" node (empty name AND empty path) is the one row that
 * does not navigate: those documents carry no folder path, so there is no
 * prefix to filter on and an empty `path_prefix` would silently show the whole
 * deposit.
 */
function ImportDepositTreeNode({ org, importUid, node, depth }: ImportDepositTreeNodeProps) {
    const { t } = useTranslation('imports')
    const [open, setOpen] = useState(depth <= DEFAULT_OPEN_DEPTH)

    const children = node.children ?? []
    const hasChildren = children.length > 0
    const unfiled = node.path === ''
    const label = unfiled ? t('tree.unfiled') : node.name

    return (
        <li data-testid={`import-tree-node-${node.path || 'unfiled'}`}>
            <div
                className="flex items-center gap-2 rounded-md py-1 pr-2 hover:bg-muted/60"
                style={{ paddingLeft: `${depth * 0.75}rem` }}
            >
                {hasChildren ? (
                    <button
                        type="button"
                        onClick={() => setOpen(value => !value)}
                        aria-label={open ? t('tree.collapse') : t('tree.expand')}
                        aria-expanded={open}
                        title={open ? t('tree.collapse') : t('tree.expand')}
                        data-testid={`import-tree-toggle-${node.path || 'unfiled'}`}
                        className="shrink-0 rounded text-muted-foreground hover:text-foreground"
                    >
                        <AppIcon path={open ? mdiChevronDown : mdiChevronRight} size="sm" />
                    </button>
                ) : (
                    <span className="w-[0.7rem] shrink-0" aria-hidden="true" />
                )}

                <AppIcon tone="muted" path={unfiled ? mdiFolderRemoveOutline : open && hasChildren ? mdiFolderOpenOutline : mdiFolderOutline} size="sm" className="shrink-0" />

                {unfiled ? (
                    <span className="min-w-0 flex-1 truncate text-sm italic text-muted-foreground">{label}</span>
                ) : (
                    <Link
                        to="/$org/documents"
                        params={{ org }}
                        search={{
                            asset_uid: undefined,
                            document_type_uid: undefined,
                            flags: undefined,
                            import_uid: importUid,
                            path_prefix: node.path,
                            limit: 100,
                        }}
                        data-testid={`import-tree-link-${node.path}`}
                        title={node.path}
                        className="min-w-0 flex-1 truncate text-sm text-foreground hover:text-primary hover:underline"
                    >
                        {label}
                    </Link>
                )}

                {node.truncated && (
                    <span
                        className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground"
                        title={t('tree.truncated_hint')}
                        data-testid={`import-tree-truncated-${node.path || 'unfiled'}`}
                    >
                        {t('tree.truncated')}
                    </span>
                )}

                <span
                    className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground"
                    data-testid={`import-tree-count-${node.path || 'unfiled'}`}
                >
                    {node.document_count}
                </span>
            </div>

            {hasChildren && open && (
                <ul>
                    {children.map(child => (
                        <ImportDepositTreeNode
                            key={child.path}
                            org={org}
                            importUid={importUid}
                            node={child}
                            depth={depth + 1}
                        />
                    ))}
                </ul>
            )}
        </li>
    )
}

/**
 * The deposit tree (spec 2026-08-16-07 §2): the client's own folder structure,
 * rebuilt from `source_details.original_path`, with a recursive document count
 * per folder. Clicking a folder opens the documents list filtered on that
 * import and that path prefix.
 *
 * It is a provenance view, not a file manager — nothing here moves or edits a
 * document.
 */
export function ImportDepositTree({
    org,
    importUid,
    tree,
    truncated = false,
    omittedFolders = 0,
    omittedDocuments = 0,
}: ImportDepositTreeProps) {
    const { t } = useTranslation('imports')

    return (
        <section data-testid="import-deposit-tree" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">{t('tree.title')}</h2>

            {tree.length === 0 ? (
                <EmptyState title={t('tree.empty')} data-testid="import-deposit-tree-empty" />
            ) : (
                <ul className="rounded-lg border border-border bg-card p-2">
                    {tree.map(node => (
                        <ImportDepositTreeNode
                            key={node.path || 'unfiled'}
                            org={org}
                            importUid={importUid}
                            node={node}
                            depth={0}
                        />
                    ))}
                </ul>
            )}

            {truncated && omittedFolders > 0 && (
                <p
                    className="text-xs text-muted-foreground"
                    data-testid="import-deposit-tree-omitted"
                >
                    {t('tree.omitted_roots', {
                        count: omittedFolders,
                        documents: omittedDocuments,
                    })}
                </p>
            )}
        </section>
    )
}
